<script setup>
// Displayed inside a modal on Product Finder
// or inside s12y section on CT Product detail.
import { computed, onMounted, provide, ref, watch } from 'vue';
import { expand, collapse } from '../../utils/expandCollapse';
import { default as t } from '../../translations/translationStrings';

// Props.
const props = defineProps({
  s12yData: {
    type: Object,
    default: () => ({
      greenhouseGasEfficiency: true,
      waterEfficiency: true,
      nutrientUseEfficiency: true,
    }),
  },
  type: {
    type: String,
    default: 'interactive',
  },
  preselectedTab: {
    type: String,
    required: false,
    default: 'greenhouseGasEfficiency',
  },
});

// Data.
const activeTabId = ref(props.preselectedTab);
const oldTabTransformOrigin = ref('left');
const newTabTransformOrigin = ref('left');
// Template ref for accordions v-for.
const accordions = ref([]);

// Computed.
const accordionKeys = computed(() => {
  return Object.keys(props.s12yData).filter((key) => props.s12yData[key]);
});

// Watch for changes on activeTabId to determine the animation direction.
if (props.type === 'modal') {
  watch(activeTabId, (newTab, oldTab) => {
    const newTabIdx = Object.keys(t.sustainableApplicationExplanation).indexOf(
      newTab,
    );
    const oldTabIdx = Object.keys(t.sustainableApplicationExplanation).indexOf(
      oldTab,
    );

    newTabTransformOrigin.value = newTabIdx > oldTabIdx ? 'left' : 'right';
    oldTabTransformOrigin.value = newTabIdx > oldTabIdx ? 'right' : 'left';
  });
}

// Watch for preselectedTab props change.
if (props.type === 'modal') {
  watch(
    () => props.preselectedTab,
    (newTab) => {
      activeTabId.value = newTab;
    },
  );
}

// Methods.
const openAccordion = (key) => {
  activeTabId.value = key;
  accordions.value.forEach((accordion) => {
    const header = accordion.querySelector('.accordion__header');
    const content = accordion.querySelector('.accordion__content');
    if (accordion.dataset.id === key) {
      expand(content, header);
    } else {
      collapse(content, header);
    }
  });
};

// Reacting to slice selects from s12y pie.
const onSliceSelect = (key) => {
  openAccordion(key);
};

// Mounted.
onMounted(() => {
  // If type is interactive, expand the first available accordion.
  if (props.type === 'interactive') {
    activeTabId.value = accordionKeys.value[0];
    onSliceSelect(activeTabId.value);
  }
});

// Provide the currently open accordion, so it can be injected by other components.
provide('openAccordionKey', activeTabId);
</script>

<template>
  <div
    class="sustainable-application-explanation"
    :class="`sustainable-application-explanation--${type}`">
    <!-- Tabs (as modal) or sustainability pie in a slot. -->
    <div
      v-if="type === 'modal'"
      class="sustainable-application-explanation__tabs">
      <div
        v-for="(item, key) in t.sustainableApplicationExplanation"
        :key="key"
        :title="item.headline"
        class="sustainable-application-explanation__tab"
        :class="{
          'sustainable-application-explanation__tab--active':
            activeTabId === key,
        }"
        :style="{
          '--transform-origin':
            activeTabId === key ? newTabTransformOrigin : oldTabTransformOrigin,
        }"
        @click="activeTabId = key">
        <img
          :src="`/${require(`../../../images/s12y-icons/icon-${key}-bg.svg`)}`"
          alt="" />
      </div>
    </div>
    <div v-else class="sustainable-application-explanation__pie">
      <slot name="pie" :onSliceSelect="onSliceSelect"></slot>
    </div>

    <!-- Text content (as modal) or accordions. -->
    <div class="sustainable-application-explanation__content">
      <template v-if="type === 'modal'">
        <template
          v-for="(content, key) in t.sustainableApplicationExplanation"
          :key="key">
          <div
            class="sustainable-application-explanation__content-item"
            :class="{
              'sustainable-application-explanation__content-item--active':
                activeTabId === key,
            }">
            <div class="sustainable-application-explanation__headline">
              {{ content.headline }}
            </div>
            <div class="sustainable-application-explanation__text">
              {{ content.text }}
            </div>
          </div>
        </template>
      </template>
      <template v-else>
        <div
          v-for="key in accordionKeys"
          :key="key"
          ref="accordions"
          :data-id="key"
          class="accordion"
          :class="{ 'accordion--open': activeTabId === key }">
          <div class="accordion__header" @click="openAccordion(key)">
            {{ t.sustainableApplicationExplanation[key].headline }}
          </div>
          <div class="accordion__content">
            {{ t.sustainableApplicationExplanation[key].text }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
